import { get, first } from 'lodash';
import i18n from '@/i18n';

import { initialState, pageOrder } from './initialState';

const isMetric = details => details.unit === 'metric';
const areaSuffix = details => i18n.t(isMetric(details) ? 'common.sqmSuffix' : 'common.sqftSuffix');
const areaAbbrev = details => i18n.t(isMetric(details) ? 'topContent.sqmAbbrev' : 'topContent.sqftAbbrev');
const areaLabel = (details, key) => (isMetric(details) ? 'details.fields.totalArea' : `details.fields.${key}`);
const areaValue = (n, details) => (n ? `${n} ${areaSuffix(details)}` : undefined);
const dollarValue = value => value && (String(value).trim().startsWith('$') ? value : `$${value}`);
const MULTI_LAYOUT = 'scissors';
const RENAMED_LAYOUTS = {
  minimalistic: 'minimal',
  serena: 'verde'
};

export const mapTourUIFromResponseToStore = (data, isBranded) => ({
  title: !isBranded && data.fullAddress ? data.fullAddress : initialState.ui.title,
  spa: getPartOfNameFromString(get(data, 'settings.layout'), 0) !== MULTI_LAYOUT,
  layout: getLayoutName(get(data, 'settings.layout')) || initialState.ui.layout,
  mode: getPartOfNameFromString(get(data, 'settings.layout'), 1) || initialState.ui.mode
});

export const mapTourDataFromResponseToStore = (data, isBranded) => ({
  id: data.id,
  settings: data.settings ? mapSettings(data.settings) : initialState.data.settings,
  pages: data.settings ? mapPages(data.settings, isBranded, data.aiVideos) : initialState.data.pages,
  fullAddress: data.fullAddress || initialState.data.fullAddress,
  address: data.address ? mapAddress(data.address) : initialState.data.address,
  details: mapDetails({...data.details, ...{address: data.fullAddress}}),
  video: [...((data.video && mapVideo(data.video)) || []), ...mapAiVideos(data.aiVideos, data.settings)],
  tour360: data.tour360 ? map360Tour(data.tour360) : [],
  videoSlideshow: data.videoSlideshow || initialState.data.videoSlideshow,
  photos: mapPhotos(data.photos || []),
  floorplanPhotos: mapPhotos(get(data, 'floorplan.photos') || []),
  floorplanFiles: get(data, 'floorplan.files') || [],
  floorplanCubicasa: get(data, 'floorplan.cubicasa') || [],
  documents: data.documents || initialState.data.documents, // [{name, url}]
  map: data.map || initialState.data.map,
  walkScore: data.walkScore ? mapWalkScore(data.walkScore) : initialState.data.walkScore,
  agent: data.agent ? mapAgent(data.agent) : initialState.data.agent,
  multiAgents: data.multiAgents ? data.multiAgents.map(mapAgent) : initialState.data.multiAgents,
  footer: data.footer ? mapFooter(data.footer) : initialState.data.footer,
  payments: data.payments ? {...initialState.data.payments, ...data.payments} : initialState.data.payments,
  branding: data.branding || initialState.data.branding
});

const mapWalkScore = data => ({
  address: data.address,
  enableWalkScore: 'enableWalkScore' in data ? data.enableWalkScore : initialState.data.walkScore.enableWalkScore,
  enableHoodq: data.enableHoodq,
  hoodqUrl: data.hoodqUrl
});

const mapSettings = data => ({
  enabled: !!data.enabled,
  sold: !!data.sold,
  videoSlideshow: !!data.videoSlideshow,
  video: !!data.video,
  slideshow: !!data.slideshow,
  tour360: !!data.top360,
  gallery: !!data.gallery,
  details: !!data.details,
  floorplan: !!data.floorplan,
  documents: !!data.documents,
  map: !!data.map,
  walkScore: !!data.walkScore,
  topSection: mapTopSection(get(data, 'topSection.value')) || initialState.data.settings.topSection,
  topSectionPhotos: mapPhotos(get(data, 'topSectionPhotos') || []) || initialState.data.settings.topSectionPhotos,
  numberOfPhotosOnIntro: parseInt(data.numberOfPhotosOnIntro) || initialState.data.settings.numberOfPhotosOnIntro,
  slideshowConfig: data.slideshowConfig || initialState.data.settings.slideshowConfig,
  layoutConfig: data.layoutConfig || initialState.data.settings.layoutConfig,
  music: get(data, 'music.url') || initialState.data.settings.music,
  exitPopup: !!data.exitPopup,
  leadCapture: data.leadCapture || initialState.data.settings.leadCapture,
  openHouse: data.openHouse ? mapOpenHouse(data.openHouse) : initialState.data.settings.openHouse,
  access: data.access || initialState.data.settings.access
});

// video, slideshow, tour360 -> 360-tour, none
const mapTopSection = value => value === 'tour360' ? '360-tour' : value;

const mapPages = (settings, isBranded, aiVideos) => ([
  { componentName: 'Details', value: 'details', label: 'pages.details', visible: !!settings.details, order: getPageOrder(settings.layout).details },
  { componentName: 'Video', value: 'video', label: 'pages.video', visible: !!settings.video || mapAiVideos(aiVideos, settings).length > 0, order: getPageOrder(settings.layout).video },
  { componentName: 'Gallery', value: 'gallery', label: 'pages.gallery', visible: !!settings.gallery, order: getPageOrder(settings.layout).gallery },
  { componentName: 'Tour360', value: '360-tour', label: 'pages.tour360', visible: !!settings.tour360, order: getPageOrder(settings.layout)['360-tour'] },
  { componentName: 'VideoSlideshow', value: 'video-slideshow', label: 'pages.videoSlideshow', visible: !!settings.videoSlideshow, order: getPageOrder(settings.layout)['video-slideshow'] },
  { componentName: 'Slideshow', value: 'slideshow', label: 'pages.slideshow', visible: !!settings.slideshow, order: getPageOrder(settings.layout).slideshow },
  { componentName: 'Floorplan', value: 'floorplan', label: 'pages.floorplan', visible: !!settings.floorplan, order: getPageOrder(settings.layout).floorplan },
  { componentName: 'Documents', value: 'documents', label: 'pages.documents', visible: !!settings.documents, order: getPageOrder(settings.layout).documents },
  { componentName: 'WalkScore', value: 'walkScore', label: 'pages.walkScore', visible: !!settings.walkScore, order: getPageOrder(settings.layout).walkScore },
  { componentName: 'Map', value: 'map', label: 'pages.map', visible: !!settings.map, order: getPageOrder(settings.layout).map },
  { componentName: 'Contact', value: 'contact', label: 'pages.contact', visible: isBranded, order: getPageOrder(settings.layout).contact }
]);

const mapAddress = data => ({
  address: data.address,
  city: data.city,
  country: get(data, 'country.label') || initialState.data.address.country,
  shortStateName: get(data, 'state.value') || data.state,
  longStateName: get(data, 'state.label') || data.state,
  zipcode: data.zipcode
});

const mapDetails = details => ({
  address: { label: 'details.fields.address', value: details.address },
  description: { label: 'details.fields.description', value: details.description },
  propertyType: { label: 'details.fields.propertyType', value: details.propertyType },
  buildingType: { label: 'details.fields.buildingType', value: details.buildingType },
  heatingType: { label: 'details.fields.heatingType', value: details.heatingType },
  price: { label: 'details.fields.price', value: dollarValue(details.price) },
  parking: { label: 'details.fields.parking', value: details.parking },
  bedrooms: { label: 'details.fields.bedrooms', value: details.bedrooms },
  bathrooms: { label: 'details.fields.bathrooms', value: details.bathrooms },
  basement: { label: 'details.fields.basement', value: yesOrNo(details.basement) },
  stories: { label: 'details.fields.stories', value: details.stories },
  appliances: { label: 'details.fields.appliances', value: details.appliances },
  garage: { label: 'details.fields.garage', value: yesOrNo(details.garage) },
  lot: { label: 'details.fields.lot', value: details.lot },
  totalSquareFootage: { label: areaLabel(details, 'totalSquareFootage'), value: areaValue(details.totalSquareFootage, details), amount: details.totalSquareFootage, suffix: areaSuffix(details), abbrev: areaAbbrev(details) },
  yearBuilt: { label: 'details.fields.yearBuilt', value: details.yearBuilt },
  taxes: { label: 'details.fields.taxes', value: details.taxes },
  maintenanceFee: { label: 'details.fields.maintenanceFee', value: dollarValue(details.maintenanceFee) },
  locker: { label: 'details.fields.locker', value: details.locker },
  guestHome: { label: 'details.fields.guestHome', value: yesOrNo(details.guestHome) },
  bedroomsGH: { label: 'details.fields.bedroomsGH', value: details.guestHome && details.bedroomsGH },
  bathroomsGH: { label: 'details.fields.bathroomsGH', value: details.guestHome && details.bathroomsGH },
  totalSquareFootageGH: { label: areaLabel(details, 'totalSquareFootageGH'), value: details.guestHome && areaValue(details.totalSquareFootageGH, details) },
  shop: { label: 'details.fields.shop', value: yesOrNo(details.shop) },
  totalSquareFootageShop: { label: areaLabel(details, 'totalSquareFootageShop'), value: details.shop && areaValue(details.totalSquareFootageShop, details) },
  ...mapAdditionalFields(details.additionalFields)
});

const mapAdditionalFields = (fields = []) =>
  (fields || [])
    .filter(field => field && field.label && field.value !== '' && field.value != null && field.value !== false)
    .reduce((acc, field, index) => ({
      ...acc,
      [`additionalField${index}`]: {
        label: field.label,
        value: field.type === 'switch' ? yesOrNo(true) : field.value
      }
    }), {});

export const mapVideo = data => {
  if (data.videos) {
    return data.videos;
  } else if (data.isYoutube || data.isVimeo || data.isIframe) {
    const name = (data.isYoutube && 'youtube') || (data.isVimeo && 'vimeo') || (data.isIframe && 'iframe');

    return [{
      name,
      youtube: {
        branded: name === 'youtube' ? data.youtubeUrlBranded : '',
        unbranded: name === 'youtube' ? data.youtubeUrlUnbranded : ''
      },
      vimeo: {
        branded: name === 'vimeo' ? data.vimeoUrlBranded : '',
        unbranded: name === 'vimeo' ? data.vimeoUrlUnbranded : ''
      },
      iframe: {
        branded: name === 'iframe' ? data.iframeUrlBranded : '',
        unbranded: name === 'iframe' ? data.iframeUrlUnbranded : ''
      }
    }];
  }
};

export const mapAiVideos = (videos, settings) =>
  (get(settings, 'aiVideos.tourPage') || [])
    .map(url => url && (videos || []).find(video => video.url === url))
    .filter(Boolean)
    .map(({ url, ratio }) => ({ name: 'file', file: { branded: url, unbranded: url, vertical: ratio !== 'landscape' } }));

const map360Tour = data => {
  if (data.tours) {
    return data.tours;
  } else if (data.matterport || data.iguide || data.cloudPano) {
    const name = (data.matterport && 'matterport') || (data.iguide && 'iguide') || (data.cloudPano && 'cloudPano');

    return [{
      name,
      matterport: name === 'matterport' ? data.matterportUrl : '',
      iguide: name === 'iguide' ? data.iguideUrl : '',
      cloudPano: name === 'cloudPano' ? data.cloudPanoUrl : ''
    }];
  }
};

const mapAgent = data => ({
  id: data.id,
  userId: data.userId,
  agentName: data.agentName,
  agentTitle: data.agentTitle,
  brokerageName: data.brokerageName,
  avatar: first(mapPhotos(data.avatar || [])),
  logo: first(mapPhotos(data.logo || [])),
  address: data.address,
  city: data.city,
  country: get(data, 'country.label') || initialState.data.agent.country,
  state: data.state,
  zipcode: data.zipcode,
  email: data.email,
  phone: data.phone,
  www: data.www,
  socials: data.socials || []
});

const mapFooter = data => ({
  companyName: data.companyName,
  companyURL: data.companyURL,
  copyrightInfo: data.copyrightInfo,
  privacyPolicy: data.privacyPolicy,
  termsAndConditions: data.termsAndConditions,
  poweredBy: data.poweredBy === false ? false : true,
  termsPrivacyOff: data.termsPrivacyOff
});

const mapPhotos = photos =>
  (Array.isArray(photos) ? photos : []).map(item => ({
    original: get(item.urls.find(el => el.size === 'orig'), 'url'),
    hidden: !!item.hidden,
    xs: get(item.urls.find(el => el.size === '500x380'), 'url'),
    md: get(item.urls.find(el => el.size === '1024x1024'), 'url'),
    xl: get(item.urls.find(el => el.size === '2560x1440'), 'url')
      || get(item.urls.find(el => el.size === '1920x1080'), 'url')
      || get(item.urls.find(el => el.size === '1600x900'), 'url')
  })).filter(item => !item.hidden);

const mapOpenHouse = data => {
  const now = new Date();

  data['days'] = data['days'].filter(item => {
    const day = new Date(item.day);
    const end = new Date(item.end);
    const eventEnd = new Date(day);

    eventEnd.setHours(end.getHours(), end.getMinutes(), end.getSeconds(), end.getMilliseconds());

    return eventEnd >= now;
  });

  return data;
};

const getPartOfNameFromString = (name, index, separator = '-') => name && name.split(separator)[index];

const yesOrNo = value => {
  if (value === undefined) {
    return undefined;
  }

  return value ? 'Yes' : 'No';
};

const getLayoutName = (layout) => {
  const name = getPartOfNameFromString(layout, 0);

  return RENAMED_LAYOUTS[name] || name;
};

const getPageOrder = (layout) => {
  const layoutName = getLayoutName(layout);

  return layoutName in pageOrder ? pageOrder[layoutName] : pageOrder.default;
};
